import { PureComponent } from 'react';
import { createBrowserHistory, History } from 'history';
import { Router as BrowserRouter } from 'react-router';
import { Helmet } from 'react-helmet';
import SiteRoutes from './SiteRoutes';
import OrgObserver from 'client/components/OrgObserver';
import SessionObserver from 'client/components/SessionObserver';
import { track } from 'shared/util/analytics/track';
import { detect } from 'detect-browser';
import ThemeContext from 'shared/util/ThemeContext';
import SendEvents from 'shared/util/analytics/SendEvents';
import { User } from 'shared/state/misc/oidc';
import { isMobile, isTablet, isDesktop, osName } from 'react-device-detect';
import axios from 'axios';
import {
  ThemeProvider as MUIThemeProvider,
  createTheme,
  CssBaseline,
} from '@mui/material';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { muiTheme } from 'shared/muiThemeOverrides';
import { getPaletteModeFromSiteTheme } from 'shared/util/themePreference';
import { resolveLegacyThemeFromSite } from 'builder/util/resolveTheme';
import { applyPaletteModeToLegacyTheme } from 'shared/util/applyPaletteModeToTheme';

const publishedThemeFallback = resolveLegacyThemeFromSite({
  enabledTheme: 'dark',
});

export interface SiteRootProps {
  siteId: number;
  basename: string;
  site: Site;
  pages: Array<Page>;
  tracking?: any;
  user?: User;
}

interface SiteRootState {
  paletteMode?: 'light' | 'dark';
}

class SiteRoot extends PureComponent<SiteRootProps, SiteRootState> {
  private history: History;
  private startTime: number;
  private sessionStartTime: number;
  private sessionEndTime: number;
  private sessionTimeout: number;
  private sessionTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: SiteRootProps) {
    super(props);
    this.sessionStartTime = Date.now();
    this.sessionEndTime = Date.now();
    this.sessionTimeout = 0.5 * 60 * 1000; // 5 minutes
    const { basename } = props;
    this.history = createBrowserHistory({
      basename,
      forceRefresh: false,
    });
  }

  private startSession() {
    this.sessionStartTime = Date.now();
    this.resetSessionTimeout();
  }

  private updateSession() {
    this.sessionEndTime = Date.now();
    this.resetSessionTimeout();
  }

  private endSession() {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
    }
    const sessionDuration = this.sessionEndTime - this.sessionStartTime;
    this.props.tracking.trackEvent({
      action: 'user_session_duration',
      sessionDuration: sessionDuration / 1000 + ' seconds',
    });
  }

  private resetSessionTimeout() {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
    }
    this.sessionTimeoutId = setTimeout(
      () => this.endSession(),
      this.sessionTimeout
    );
  }

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.endSession();
    }
  };

  handleUserActivity = () => {
    this.updateSession();
  };

  componentDidMount(): void {
    this.startTime = Date.now();
    try {
      // Published sites ignore shared cookies/localStorage; palette is site-config driven.
      const siteMode = this.props.site?.theme
        ? getPaletteModeFromSiteTheme(this.props.site.theme as any)
        : 'dark';
      this.setState({ paletteMode: siteMode ?? 'dark' });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('SiteRoot: Failed to initialize palette mode', err);
      }
      // ignore
    }

    const fetchIPInfo = async () => {
      try {
        // geoJs is a free IP geolocation API https://www.geojs.io/docs/general/
        const response = await axios.get('https://get.geojs.io/v1/ip/geo.json');
        const data = response.data;
        this.props.tracking.trackEvent({
          action: 'geo_location',
          location: {
            city: data.city,
            country: data.country,
            region: data.region,
          },
        });
      } catch (error) {
        console.error('Error fetching IP info', error);
      }
    };
    fetchIPInfo();
    this.startSession();

    window.addEventListener('mousemove', this.handleUserActivity);
    window.addEventListener('keydown', this.handleUserActivity);
    window.addEventListener('click', this.handleUserActivity);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    window.addEventListener('beforeunload', () => {
      this.endSession();
    });
  }

  componentDidUpdate(prevProps: SiteRootProps): void {
    const prevTheme = prevProps.site?.theme;
    const currTheme = this.props.site?.theme;
    if (currTheme && currTheme !== prevTheme) {
      try {
        const mode = getPaletteModeFromSiteTheme(currTheme as any);
        this.setState({ paletteMode: mode });
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error(
            'SiteRoot: Failed to update palette mode on theme change',
            err
          );
        }
        // ignore
      }
    }
  }

  componentWillUnmount() {
    // Calculate the duration and track the event
    const endTime = Date.now();
    const duration = endTime - this.startTime;
    this.props.tracking.trackEvent({
      action: 'visit_duration',
      visitDuration: new Date(duration).toLocaleDateString(),
    });
    window.removeEventListener('mousemove', this.handleUserActivity);
    window.removeEventListener('keydown', this.handleUserActivity);
    window.removeEventListener('click', this.handleUserActivity);
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
    this.endSession();
  }

  render() {
    const { site, user } = this.props;
    const siteTheme = site?.theme || publishedThemeFallback;

    const siteMode = getPaletteModeFromSiteTheme(siteTheme as any);
    const paletteMode = this.state?.paletteMode ?? siteMode ?? 'dark';

    const fontFamilyOverride =
      (siteTheme as any)?.font?.family?.base?.value ||
      muiTheme.typography.fontFamily;
    // keep legacy shared theme updated for any direct consumers/tests
    (muiTheme as any).palette = {
      ...(muiTheme as any).palette,
      mode: paletteMode,
    };
    const appliedMuiTheme = createTheme({
      ...muiTheme,
      typography: { ...muiTheme.typography, fontFamily: fontFamilyOverride },
      palette: { ...(muiTheme as any).palette, mode: paletteMode },
    } as any);

    const resolvedTheme = resolveLegacyThemeFromSite(siteTheme as any);
    const styledTheme = applyPaletteModeToLegacyTheme(
      resolvedTheme as any,
      paletteMode as 'light' | 'dark'
    );

    return (
      <BrowserRouter history={this.history}>
        <MUIThemeProvider theme={appliedMuiTheme}>
          <CssBaseline />
          <StyledThemeProvider theme={styledTheme}>
            <ThemeContext.Provider
              value={{
                theme: styledTheme as any,
              }}
            >
              <Helmet>
                <title>{site.name}</title>
                {(siteTheme as any)?.favicon && (
                  <link
                    rel="shortcut icon"
                    href={(siteTheme as any).favicon.url}
                  />
                )}
              </Helmet>
              <OrgObserver />
              <SessionObserver />
              <div data-test-id="send-events" />
              {user ? <SendEvents site={site} /> : null}
              <SiteRoutes siteId={site.siteId} />
            </ThemeContext.Provider>
          </StyledThemeProvider>
        </MUIThemeProvider>
      </BrowserRouter>
    );
  }
}

export default track<SiteRootProps>(props => {
  let deviceType: string;
  if (isMobile) {
    deviceType = 'Mobile';
  } else if (isTablet) {
    deviceType = 'Tablet';
  } else if (isDesktop) {
    deviceType = 'Desktop';
  } else {
    deviceType = 'Unknown';
  }

  return {
    timestamp: new Date(),
    domain: props.site.domain,
    siteId: props.siteId,
    browser: detect(),
    deviceType,
    operatingSystem: osName,
  };
})(SiteRoot);
